// src/pages/InventoryAdmin.js
// 後台點貨管理：三個子頁籤 — 明細 / 叫貨 / 位置

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import socket from '../lib/socket';
import InventoryOrderView from './InventoryOrderView';
import SalesConsumptionView from './SalesConsumptionView';
import SalesRevenueView from './SalesRevenueView';

function todayYmd() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

const api = (url, opts) => fetch(url, { headers: { 'Content-Type': 'application/json' }, ...opts }).then(r => r.json());

/** 0=日 … 6=六，與後端 / Date#getDay() 一致；新增品項預設：日、二、四 */
const WD_LABEL = ['日', '一', '二', '三', '四', '五', '六'];
const DEFAULT_ITEM_WEEKDAYS = [0, 2, 4];

function formatWeekdaysDisplay(ws) {
  if (!ws || !ws.length) return '—';
  return [...ws].sort((a, b) => a - b).map(d => WD_LABEL[d]).join('、');
}

function round1(n) {
  const x = Number(n);
  if (!Number.isFinite(x)) return 0;
  return Math.round((x + Number.EPSILON) * 10) / 10;
}

function formatQty(n) {
  const x = Number(n);
  if (!Number.isFinite(x)) return '0';
  return String(round1(x)).replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '');
}

function WeekdayCheckboxes({ value, onChange }) {
  const v = value || [];
  const toggle = (d) => {
    const set = new Set(v);
    if (set.has(d)) set.delete(d);
    else set.add(d);
    onChange([...set].sort((a, b) => a - b));
  };
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 12px', alignItems: 'center' }}>
      {WD_LABEL.map((label, d) => (
        <label key={d} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, cursor: 'pointer', fontSize: 14, fontWeight: 600, color: '#334155', userSelect: 'none' }}>
          <input type="checkbox" checked={v.includes(d)} onChange={() => toggle(d)} style={{ width: 16, height: 16 }} />
          {label}
        </label>
      ))}
    </div>
  );
}

// 通用下拉 + 自行輸入複合元件（廠商、單位共用）
function ComboSelect({ value, options, onChange, style, placeholder = '選擇', newLabel = '＋ 新增' }) {
  const [custom, setCustom] = useState(false);
  const isCustom = custom || (value && !options.includes(value));

  if (isCustom) {
    return (
      <div style={{ display: 'flex', gap: 4, width: '100%' }}>
        <input
          value={value || ''}
          onChange={e => onChange(e.target.value)}
          placeholder={placeholder}
          style={{ ...style, flex: 1 }}
          autoFocus
        />
        {options.length > 0 && (
          <button type="button" onClick={() => { setCustom(false); onChange(''); }}
            style={{ padding: '0 8px', borderRadius: 4, border: '1px solid #d1d5db', background: '#f9fafb', fontSize: 14, cursor: 'pointer', whiteSpace: 'nowrap' }}>
            選單
          </button>
        )}
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', gap: 4, width: '100%' }}>
      <select value={value || ''} onChange={e => { if (e.target.value === '__new__') { setCustom(true); onChange(''); } else onChange(e.target.value); }} style={{ ...style, flex: 1 }}>
        <option value="">{placeholder}</option>
        {options.map(v => <option key={v} value={v}>{v}</option>)}
        <option value="__new__">{newLabel}</option>
      </select>
    </div>
  );
}

// ========== 明細頁 ==========
function ItemsTab({ items, locations, vendors: vendorList = [], reload, isMobile }) {
  const [filter, setFilter] = useState('');
  const [filterLoc, setFilterLoc] = useState('');
  const [filterVendor, setFilterVendor] = useState('');
  /** 篩選「排程含此週幾」：空字串＝全部 */
  const [filterWeekday, setFilterWeekday] = useState('');
  const [sortBy, setSortBy] = useState('location');
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [showAdd, setShowAdd] = useState(false);
  const [showBatch, setShowBatch] = useState(false);
  const [batchText, setBatchText] = useState('');
  const [batchResult, setBatchResult] = useState(null);
  const [batchLoading, setBatchLoading] = useState(false);
  const fileInputRef = useRef(null);

  const activeVendors = useMemo(
    () => (vendorList || []).filter(v => v.is_active !== false).sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    [vendorList],
  );
  const vendorFilterOptions = useMemo(
    () => [...new Set(items.map(i => i.vendor).filter(Boolean))].sort(),
    [items],
  );
  const units = useMemo(() => [...new Set(items.map(i => i.unit).filter(Boolean))].sort(), [items]);
  const filtered = useMemo(() => {
    const list = items.filter(item => {
      if (filter && !item.name.toLowerCase().includes(filter.toLowerCase())) return false;
      if (filterLoc && String(item.location_id) !== filterLoc) return false;
      if (filterVendor && item.vendor !== filterVendor) return false;
      if (filterWeekday !== '') {
        const wd = parseInt(filterWeekday, 10);
        const wds = item.weekdays || [];
        if (!wds.includes(wd)) return false;
      }
      return true;
    });
    const cmp = (a, b, key) => (a[key] || '').localeCompare(b[key] || '');
    const weekdayKey = (it) => (it.weekdays || []).slice().sort((x, y) => x - y).join(',');
    switch (sortBy) {
      case 'vendor': return list.sort((a, b) => cmp(a, b, 'vendor') || cmp(a, b, 'name'));
      case 'name': return list.sort((a, b) => cmp(a, b, 'name'));
      case 'weekday': return list.sort((a, b) => weekdayKey(a).localeCompare(weekdayKey(b)) || cmp(a, b, 'name'));
      case 'location':
      default: return list.sort((a, b) => cmp(a, b, 'location_name') || cmp(a, b, 'name'));
    }
  }, [items, filter, filterLoc, filterVendor, filterWeekday, sortBy]);

  const startEdit = (item) => {
    setEditing(item.id);
    setForm({
      ...item,
      safety_line: item.safety_line == null ? '' : String(item.safety_line),
      standing_stock: item.standing_stock == null ? '' : String(item.standing_stock),
      max_stock: item.max_stock == null ? '' : String(item.max_stock),
      unit_price: item.unit_price == null ? '' : String(item.unit_price),
      vendor_item_name: item.vendor_item_name || '',
      vendor_id: item.vendor_id != null ? item.vendor_id : '',
    });
  };
  const cancelEdit = () => { setEditing(null); setForm({}); };
  const saveEdit = async () => {
    const payload = {
      ...form,
      safety_line: form.safety_line === '' ? 0 : (parseFloat(form.safety_line) || 0),
      standing_stock: form.standing_stock === '' || form.standing_stock == null ? null : (parseFloat(form.standing_stock) || 0),
      max_stock: form.max_stock === '' || form.max_stock == null ? null : (parseFloat(form.max_stock) || 0),
      unit_price: form.unit_price === '' || form.unit_price == null ? null : (parseFloat(form.unit_price) || 0),
      vendor_id: form.vendor_id === '' || form.vendor_id == null ? null : Number(form.vendor_id),
      vendor_item_name: form.vendor_item_name || '',
    };
    await api(`/api/inventory/items/${editing}`, { method: 'PUT', body: JSON.stringify(payload) });
    setEditing(null); reload();
  };
  const toggleActive = async (item) => {
    await api(`/api/inventory/items/${item.id}`, { method: 'PUT', body: JSON.stringify({ is_active: !item.is_active }) });
    reload();
  };
  const deleteItem = async (item) => {
    if (!window.confirm(`確定刪除「${item.name}」？`)) return;
    await api(`/api/inventory/items/${item.id}`, { method: 'DELETE' }); reload();
  };
  const addItem = async () => {
    if (!form.name?.trim()) return alert('請輸入名稱');
    const payload = {
      ...form,
      safety_line: form.safety_line === '' ? 0 : (parseFloat(form.safety_line) || 0),
      standing_stock: form.standing_stock === '' || form.standing_stock == null ? null : (parseFloat(form.standing_stock) || 0),
      max_stock: form.max_stock === '' || form.max_stock == null ? null : (parseFloat(form.max_stock) || 0),
      unit_price: form.unit_price === '' || form.unit_price == null ? null : (parseFloat(form.unit_price) || 0),
      vendor_id: form.vendor_id === '' || form.vendor_id == null ? null : Number(form.vendor_id),
      vendor_item_name: form.vendor_item_name || '',
    };
    await api('/api/inventory/items', { method: 'POST', body: JSON.stringify(payload) });
    setShowAdd(false); setForm({}); reload();
  };

  const parseBatchText = (text) => {
    const looksLikeWeekdays = (s) => {
      const t = (s || '').trim();
      return t.startsWith('[') || t.includes('點貨');
    };
    return text.split('\n').filter(line => line.trim()).map(line => {
      const cols = line.includes('\t') ? line.split('\t') : line.split(',');
      const safety_line = (cols[4] || '').trim();
      let standing_stock = '';
      let max_stock = '';
      let tag = '';
      let vendor_item_name = '';
      let unit_price = '';
      if (cols.length >= 10) {
        standing_stock = (cols[5] || '').trim();
        max_stock = (cols[6] || '').trim();
        tag = (cols[7] || '').trim();
        vendor_item_name = (cols[8] || '').trim();
        unit_price = (cols[9] || '').trim();
      } else if (cols.length === 9) {
        standing_stock = (cols[5] || '').trim();
        max_stock = (cols[6] || '').trim();
        tag = (cols[7] || '').trim();
        vendor_item_name = (cols[8] || '').trim();
      } else if (cols.length >= 8) {
        standing_stock = (cols[5] || '').trim();
        max_stock = (cols[6] || '').trim();
        tag = (cols[7] || '').trim();
      } else if (cols.length === 7) {
        if (looksLikeWeekdays(cols[6])) {
          max_stock = (cols[5] || '').trim();
          tag = (cols[6] || '').trim();
        } else {
          standing_stock = (cols[5] || '').trim();
          max_stock = (cols[6] || '').trim();
        }
      } else {
        max_stock = (cols[5] || '').trim();
      }
      return {
        name: (cols[0] || '').trim(),
        location: (cols[1] || '').trim(),
        vendor: (cols[2] || '').trim(),
        unit: (cols[3] || '').trim(),
        safety_line,
        standing_stock,
        max_stock,
        tag,
        vendor_item_name,
        unit_price,
      };
    });
  };

  const submitBatch = async () => {
    const rows = parseBatchText(batchText);
    if (rows.length === 0) return alert('請貼上至少一行資料');
    setBatchLoading(true); setBatchResult(null);
    try {
      const res = await api('/api/inventory/items/batch-import', {
        method: 'POST', body: JSON.stringify({ items: rows }),
      });
      setBatchResult(res);
      if (res.created > 0) reload();
    } catch (e) { setBatchResult({ success: false, error: e.message }); }
    finally { setBatchLoading(false); }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      const text = evt.target.result;
      setBatchText(text.trim());
      setBatchResult(null);
    };
    reader.readAsText(file, 'UTF-8');
    e.target.value = '';
  };

  const inputStyle = {
    padding: '6px 10px', borderRadius: 6, border: '1px solid #d1d5db',
    fontSize: 15, width: '100%', boxSizing: 'border-box',
  };

  return (
    <div>
      {/* 篩選列 */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
        <input placeholder="搜尋名稱…" value={filter} onChange={e => setFilter(e.target.value)} style={{ ...inputStyle, width: isMobile ? '100%' : 180 }} />
        <select value={filterLoc} onChange={e => setFilterLoc(e.target.value)} style={{ ...inputStyle, width: isMobile ? 'calc(50% - 4px)' : 140 }}>
          <option value="">全部位置</option>
          {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
        </select>
        <select value={filterVendor} onChange={e => setFilterVendor(e.target.value)} style={{ ...inputStyle, width: isMobile ? 'calc(50% - 4px)' : 140 }}>
          <option value="">全部廠商</option>
          {vendorFilterOptions.map(v => <option key={v} value={v}>{v}</option>)}
        </select>
        <select value={filterWeekday} onChange={e => setFilterWeekday(e.target.value)} style={{ ...inputStyle, width: isMobile ? 'calc(50% - 4px)' : 150 }}>
          <option value="">全部排程</option>
          {WD_LABEL.map((label, d) => (
            <option key={d} value={String(d)}>含週{label}</option>
          ))}
        </select>
        <div style={{
          display: 'inline-flex', borderRadius: 6, overflow: 'hidden',
          border: '1px solid #d1d5db', flexShrink: 0,
        }}>
          {[
            { key: 'location', label: isMobile ? '位置' : '位置排序' },
            { key: 'vendor', label: isMobile ? '廠商' : '廠商排序' },
            { key: 'weekday', label: isMobile ? '星期' : '星期排序' },
            { key: 'name', label: isMobile ? '名稱' : '名稱排序' },
          ].map(opt => (
            <button
              key={opt.key}
              type="button"
              onClick={() => setSortBy(opt.key)}
              style={{
                padding: isMobile ? '6px 8px' : '6px 12px', border: 'none', cursor: 'pointer',
                fontSize: isMobile ? 13 : 14, fontWeight: 600,
                background: sortBy === opt.key ? '#1e293b' : '#fff',
                color: sortBy === opt.key ? '#fff' : '#64748b',
                transition: 'all .15s',
              }}
            >{opt.label}</button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 8, ...(isMobile ? { width: '100%' } : { marginLeft: 'auto' }) }}>
          <button type="button" onClick={() => { setShowBatch(true); setBatchText(''); setBatchResult(null); }}
            style={{ padding: isMobile ? '7px 12px' : '8px 18px', borderRadius: 6, border: '1px solid #2563eb', background: '#fff', color: '#2563eb', fontWeight: 700, fontSize: isMobile ? 14 : 15, cursor: 'pointer', flex: isMobile ? 1 : undefined }}>
            批次匯入
          </button>
          <button type="button" onClick={() => { setShowAdd(true); setForm({ is_active: true, weekdays: [...DEFAULT_ITEM_WEEKDAYS], safety_line: '', standing_stock: '', max_stock: '', vendor_item_name: '', unit_price: '', vendor_id: '' }); }}
            style={{ padding: isMobile ? '7px 12px' : '8px 18px', borderRadius: 6, border: 'none', background: '#2563eb', color: '#fff', fontWeight: 700, fontSize: isMobile ? 14 : 15, cursor: 'pointer', flex: isMobile ? 1 : undefined }}>
            + 新增商品
          </button>
        </div>
      </div>

      {/* 新增表單 */}
      {showAdd && (
        <div style={{ background: '#eff6ff', borderRadius: 10, padding: 16, marginBottom: 14, border: '1px solid #bfdbfe' }}>
          <div style={{ fontWeight: 700, marginBottom: 10, fontSize: 16 }}>新增商品</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 10 }}>
            <input placeholder="名稱 *" value={form.name || ''} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} style={inputStyle} />
            <select value={form.location_id || ''} onChange={e => setForm(p => ({ ...p, location_id: +e.target.value || null }))} style={inputStyle}>
              <option value="">選擇位置</option>
              {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
            <select value={form.vendor_id ?? ''} onChange={e => setForm(p => ({ ...p, vendor_id: e.target.value === '' ? '' : +e.target.value }))} style={inputStyle}>
              <option value="">選擇廠商</option>
              {activeVendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
            </select>
            <input placeholder="廠商品名" value={form.vendor_item_name || ''} onChange={e => setForm(p => ({ ...p, vendor_item_name: e.target.value }))} style={inputStyle} />
            <ComboSelect value={form.unit || ''} options={units} onChange={v => setForm(p => ({ ...p, unit: v }))} style={inputStyle} placeholder="選擇單位" newLabel="＋ 新增單位" />
            <input placeholder="單價" type="number" step="0.01" inputMode="decimal" value={form.unit_price ?? ''} onChange={e => setForm(p => ({ ...p, unit_price: e.target.value }))} style={inputStyle} />
            <input placeholder="安全線" type="number" step="0.1" inputMode="decimal" value={form.safety_line ?? ''} onChange={e => setForm(p => ({ ...p, safety_line: e.target.value }))} style={inputStyle} />
            <input placeholder="常備庫存量" type="number" step="0.1" inputMode="decimal" value={form.standing_stock ?? ''} onChange={e => setForm(p => ({ ...p, standing_stock: e.target.value }))} style={inputStyle} />
            <input placeholder="最大庫存量" type="number" step="0.1" inputMode="decimal" value={form.max_stock ?? ''} onChange={e => setForm(p => ({ ...p, max_stock: e.target.value }))} style={inputStyle} />
          </div>
          <div style={{ marginTop: 12 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#475569', marginBottom: 8 }}>點貨星期（預設：日、二、四；可複選）</div>
            <WeekdayCheckboxes
              value={form.weekdays || DEFAULT_ITEM_WEEKDAYS}
              onChange={ws => setForm(p => ({ ...p, weekdays: ws }))}
            />
          </div>
          <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
            <button type="button" onClick={addItem} style={{ padding: '7px 20px', borderRadius: 6, border: 'none', background: '#16a34a', color: '#fff', fontWeight: 700, fontSize: 15, cursor: 'pointer' }}>確定新增</button>
            <button type="button" onClick={() => { setShowAdd(false); setForm({}); }} style={{ padding: '7px 20px', borderRadius: 6, border: '1px solid #d1d5db', background: '#fff', color: '#374151', fontWeight: 600, fontSize: 15, cursor: 'pointer' }}>取消</button>
          </div>
        </div>
      )}

      {/* 批次匯入 */}
      {showBatch && (
        <div style={{ background: '#fefce8', borderRadius: 10, padding: 16, marginBottom: 14, border: '1px solid #fde047' }}>
          <div style={{ fontWeight: 700, marginBottom: 6, fontSize: 16 }}>批次匯入商品</div>
          <div style={{ fontSize: 14, color: '#64748b', marginBottom: 10, lineHeight: 1.6 }}>
            上傳 CSV 檔案或直接貼上文字，每行一筆，欄位用 <b>Tab</b> 或 <b>逗號</b> 分隔。<br />
            格式：<code style={{ background: '#fff', padding: '2px 6px', borderRadius: 4, fontSize: 13 }}>名稱, 位置, 廠商, 單位, 安全線, 常備, 最大, 星期, 廠商品名(選填), 單價(選填)</code><br />
            位置／廠商名稱需與系統一致；找不到的廠商名稱會自動建立。
          </div>
          <div style={{ marginBottom: 8 }}>
            <input ref={fileInputRef} type="file" accept=".csv,.tsv,.txt" onChange={handleFileUpload} style={{ display: 'none' }} />
            <button type="button" onClick={() => fileInputRef.current?.click()}
              style={{
                padding: '6px 16px', borderRadius: 6, border: '1px solid #d1d5db',
                background: '#fff', color: '#374151', fontWeight: 700, fontSize: 14,
                cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6,
              }}>
              <span style={{ fontSize: 16 }}>📄</span> 選擇 CSV 檔案
            </button>
          </div>
          <textarea
            value={batchText}
            onChange={e => setBatchText(e.target.value)}
            placeholder={`範例：\n可樂\t吧台\t飲料商\t瓶\t10\t20\t30\n啤酒\t冰箱\t酒商\t罐\t20\t40\t50`}
            rows={8}
            style={{
              width: '100%', boxSizing: 'border-box', padding: '10px 12px',
              borderRadius: 8, border: '1px solid #d1d5db', fontSize: 15,
              fontFamily: 'monospace', lineHeight: 1.6, resize: 'vertical',
            }}
          />
          {batchResult && (
            <div style={{
              marginTop: 10, padding: '10px 12px', borderRadius: 8, fontSize: 14, lineHeight: 1.6,
              background: batchResult.created > 0 ? '#dcfce7' : '#fee2e2',
              border: `1px solid ${batchResult.created > 0 ? '#86efac' : '#fca5a5'}`,
            }}>
              {batchResult.success ? (
                <>
                  成功匯入 <b>{batchResult.created}</b> 筆
                  {batchResult.duplicated > 0 && <span>，跳過 {batchResult.duplicated} 筆（名稱重複）</span>}
                  {batchResult.skipped > 0 && <span>，跳過 {batchResult.skipped} 筆（名稱空白）</span>}
                  {batchResult.errors?.length > 0 && (
                    <div style={{ marginTop: 4, color: '#dc2626' }}>
                      {batchResult.errors.map((e, i) => <div key={i}>第 {e.row} 行「{e.name}」失敗：{e.error}</div>)}
                    </div>
                  )}
                </>
              ) : (
                <span style={{ color: '#dc2626' }}>匯入失敗：{batchResult.error}</span>
              )}
            </div>
          )}
          <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
            <button type="button" onClick={submitBatch} disabled={batchLoading}
              style={{ padding: '7px 20px', borderRadius: 6, border: 'none', background: '#16a34a', color: '#fff', fontWeight: 700, fontSize: 15, cursor: 'pointer', opacity: batchLoading ? 0.6 : 1 }}>
              {batchLoading ? '匯入中…' : '確定匯入'}
            </button>
            <button type="button" onClick={() => { setShowBatch(false); setBatchText(''); setBatchResult(null); }}
              style={{ padding: '7px 20px', borderRadius: 6, border: '1px solid #d1d5db', background: '#fff', color: '#374151', fontWeight: 600, fontSize: 15, cursor: 'pointer' }}>
              關閉
            </button>
          </div>
        </div>
      )}

      {/* 商品列表 */}
      {isMobile ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {filtered.map((item) => (
            editing === item.id ? (
              <div key={item.id} style={{ background: '#fffbeb', borderRadius: 10, border: '1px solid #fde68a', padding: 14 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
                  <input placeholder="名稱" value={form.name || ''} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} style={inputStyle} />
                  <select value={form.location_id || ''} onChange={e => setForm(p => ({ ...p, location_id: +e.target.value || null }))} style={inputStyle}>
                    <option value="">無位置</option>
                    {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                  </select>
                  <select value={form.vendor_id ?? ''} onChange={e => setForm(p => ({ ...p, vendor_id: e.target.value === '' ? '' : +e.target.value }))} style={inputStyle}>
                    <option value="">無廠商</option>
                    {activeVendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
                  </select>
                  <input placeholder="廠商品名" value={form.vendor_item_name || ''} onChange={e => setForm(p => ({ ...p, vendor_item_name: e.target.value }))} style={inputStyle} />
                  <ComboSelect value={form.unit || ''} options={units} onChange={v => setForm(p => ({ ...p, unit: v }))} style={inputStyle} placeholder="單位" newLabel="＋ 新增" />
                  <input type="number" step="0.01" inputMode="decimal" placeholder="單價" value={form.unit_price ?? ''} onChange={e => setForm(p => ({ ...p, unit_price: e.target.value }))} style={inputStyle} />
                  <input type="number" step="0.1" inputMode="decimal" placeholder="安全線" value={form.safety_line ?? ''} onChange={e => setForm(p => ({ ...p, safety_line: e.target.value }))} style={inputStyle} />
                  <input type="number" step="0.1" inputMode="decimal" placeholder="常備庫存量" value={form.standing_stock ?? ''} onChange={e => setForm(p => ({ ...p, standing_stock: e.target.value }))} style={inputStyle} />
                  <input type="number" step="0.1" inputMode="decimal" placeholder="最大庫存量" value={form.max_stock ?? ''} onChange={e => setForm(p => ({ ...p, max_stock: e.target.value }))} style={inputStyle} />
                </div>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#475569', marginBottom: 6 }}>點貨星期</div>
                <WeekdayCheckboxes value={form.weekdays || []} onChange={ws => setForm(p => ({ ...p, weekdays: ws }))} />
                <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                  <button type="button" onClick={saveEdit} style={btnSmall('#16a34a')}>儲存</button>
                  <button type="button" onClick={cancelEdit} style={btnSmall('#6b7280')}>取消</button>
                </div>
              </div>
            ) : (
              <div key={item.id} style={{
                background: '#fff', borderRadius: 10, border: '1px solid #e5e7eb',
                padding: '12px 14px', opacity: item.is_active ? 1 : 0.5,
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
                  <div>
                    <span style={{ fontWeight: 700, fontSize: 16 }}>{item.name}</span>
                    {item.unit && <span style={{ fontSize: 13, color: '#94a3b8', marginLeft: 6 }}>({item.unit})</span>}
                  </div>
                  <button type="button" onClick={() => toggleActive(item)} style={{
                    padding: '2px 10px', borderRadius: 999, border: 'none', fontSize: 13, fontWeight: 700, cursor: 'pointer', flexShrink: 0,
                    background: item.is_active ? '#dcfce7' : '#fee2e2',
                    color: item.is_active ? '#16a34a' : '#dc2626',
                  }}>
                    {item.is_active ? '啟用' : '停用'}
                  </button>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2px 16px', fontSize: 14, color: '#475569', marginBottom: 6 }}>
                  <span>位置：{item.location_name || '—'}</span>
                  <span>廠商：{item.vendor || '—'}</span>
                  <span>廠商品名：{item.vendor_item_name || '—'}</span>
                  <span>單價：{item.unit_price == null ? '—' : item.unit_price}</span>
                  <span>安全線：{formatQty(item.safety_line)}</span>
                  <span>常備：{item.standing_stock == null ? '—' : formatQty(item.standing_stock)}</span>
                  <span>最大：{item.max_stock == null ? '—' : formatQty(item.max_stock)}</span>
                  <span style={{ color: '#3730a3', fontWeight: 600 }}>{formatWeekdaysDisplay(item.weekdays)}</span>
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button type="button" onClick={() => startEdit(item)} style={btnSmall('#2563eb')}>編輯</button>
                  <button type="button" onClick={() => deleteItem(item)} style={btnSmall('#dc2626')}>刪除</button>
                </div>
              </div>
            )
          ))}
          {filtered.length === 0 && (
            <div style={{ textAlign: 'center', color: '#94a3b8', padding: 28 }}>無符合條件的商品</div>
          )}
        </div>
      ) : (
      <div style={{
        background: '#fff', borderRadius: 10, border: '1px solid #e5e7eb',
        overflow: 'auto', maxHeight: 'calc(100dvh - 200px)',
        WebkitOverflowScrolling: 'touch',
      }}>
        <table style={{ width: '100%', minWidth: 1180, borderCollapse: 'separate', borderSpacing: 0, fontSize: 14 }}>
          <thead>
            <tr style={{ background: '#f1f5f9', borderBottom: '2px solid #e5e7eb' }}>
              <th style={{ ...thStyle, position: 'sticky', top: 0, left: 0, zIndex: 4, background: '#f1f5f9', minWidth: 140, boxShadow: '2px 0 0 #e5e7eb' }}>名稱</th>
              <th style={{ ...thStyle, position: 'sticky', top: 0, zIndex: 2, background: '#f1f5f9', minWidth: 88 }}>位置</th>
              <th style={{ ...thStyle, position: 'sticky', top: 0, zIndex: 2, background: '#f1f5f9', minWidth: 88 }}>廠商</th>
              <th style={{ ...thStyle, position: 'sticky', top: 0, zIndex: 2, background: '#f1f5f9', minWidth: 100 }}>廠商品名</th>
              <th style={{ ...thStyle, position: 'sticky', top: 0, zIndex: 2, background: '#f1f5f9', minWidth: 56 }}>單位</th>
              <th style={{ ...thStyle, position: 'sticky', top: 0, zIndex: 2, background: '#f1f5f9', minWidth: 72, textAlign: 'right' }}>單價</th>
              <th style={{ ...thStyle, position: 'sticky', top: 0, zIndex: 2, background: '#f1f5f9', minWidth: 64, textAlign: 'right' }}>安全</th>
              <th style={{ ...thStyle, position: 'sticky', top: 0, zIndex: 2, background: '#f1f5f9', minWidth: 64, textAlign: 'right' }}>常備</th>
              <th style={{ ...thStyle, position: 'sticky', top: 0, zIndex: 2, background: '#f1f5f9', minWidth: 64, textAlign: 'right' }}>最大</th>
              <th style={{ ...thStyle, position: 'sticky', top: 0, zIndex: 2, background: '#f1f5f9', minWidth: 120 }}>點貨日</th>
              <th style={{ ...thStyle, position: 'sticky', top: 0, zIndex: 2, background: '#f1f5f9', minWidth: 72 }}>啟用</th>
              <th style={{ ...thStyle, position: 'sticky', top: 0, right: 0, zIndex: 4, background: '#f1f5f9', minWidth: 120, boxShadow: '-2px 0 0 #e5e7eb' }}>操作</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((item, idx) => (
              editing === item.id ? (
                <tr key={item.id} style={{ background: '#fffbeb' }}>
                  <td style={{ ...tdStyle, position: 'sticky', left: 0, zIndex: 1, background: '#fffbeb', boxShadow: '2px 0 0 #e5e7eb' }}><input value={form.name || ''} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} style={inputStyle} /></td>
                  <td style={tdStyle}>
                    <select value={form.location_id || ''} onChange={e => setForm(p => ({ ...p, location_id: +e.target.value || null }))} style={inputStyle}>
                      <option value="">無</option>
                      {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                    </select>
                  </td>
                  <td style={tdStyle}>
                    <select value={form.vendor_id ?? ''} onChange={e => setForm(p => ({ ...p, vendor_id: e.target.value === '' ? '' : +e.target.value }))} style={inputStyle}>
                      <option value="">無</option>
                      {activeVendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
                    </select>
                  </td>
                  <td style={tdStyle}><input value={form.vendor_item_name || ''} onChange={e => setForm(p => ({ ...p, vendor_item_name: e.target.value }))} style={inputStyle} placeholder="廠商品名" /></td>
                  <td style={tdStyle}><ComboSelect value={form.unit || ''} options={units} onChange={v => setForm(p => ({ ...p, unit: v }))} style={inputStyle} placeholder="選擇單位" newLabel="＋ 新增單位" /></td>
                  <td style={tdStyle}><input type="number" step="0.01" inputMode="decimal" placeholder="—" value={form.unit_price ?? ''} onChange={e => setForm(p => ({ ...p, unit_price: e.target.value }))} style={{ ...inputStyle, width: 72 }} /></td>
                  <td style={tdStyle}><input type="number" step="0.1" inputMode="decimal" value={form.safety_line ?? ''} onChange={e => setForm(p => ({ ...p, safety_line: e.target.value }))} style={{ ...inputStyle, width: 64 }} /></td>
                  <td style={tdStyle}><input type="number" step="0.1" inputMode="decimal" placeholder="—" value={form.standing_stock ?? ''} onChange={e => setForm(p => ({ ...p, standing_stock: e.target.value }))} style={{ ...inputStyle, width: 64 }} /></td>
                  <td style={tdStyle}><input type="number" step="0.1" inputMode="decimal" placeholder="—" value={form.max_stock ?? ''} onChange={e => setForm(p => ({ ...p, max_stock: e.target.value }))} style={{ ...inputStyle, width: 64 }} /></td>
                  <td style={{ ...tdStyle, minWidth: 120 }}>
                    <WeekdayCheckboxes value={form.weekdays || []} onChange={ws => setForm(p => ({ ...p, weekdays: ws }))} />
                  </td>
                  <td style={tdStyle}>—</td>
                  <td style={{ ...tdStyle, position: 'sticky', right: 0, zIndex: 1, background: '#fffbeb', boxShadow: '-2px 0 0 #e5e7eb', whiteSpace: 'nowrap' }}>
                    <button type="button" onClick={saveEdit} style={btnSmall('#16a34a')}>儲存</button>
                    <button type="button" onClick={cancelEdit} style={btnSmall('#6b7280')}>取消</button>
                  </td>
                </tr>
              ) : (
                <tr key={item.id} style={{ opacity: item.is_active ? 1 : 0.5, background: idx % 2 === 0 ? '#fff' : '#f8fafc' }}>
                  <td style={{ ...tdStyle, position: 'sticky', left: 0, zIndex: 1, background: idx % 2 === 0 ? '#fff' : '#f8fafc', boxShadow: '2px 0 0 #e5e7eb' }}><span style={{ fontWeight: 600 }}>{item.name}</span></td>
                  <td style={tdStyle}>{item.location_name || '—'}</td>
                  <td style={tdStyle}>{item.vendor || '—'}</td>
                  <td style={tdStyle}>{item.vendor_item_name || '—'}</td>
                  <td style={tdStyle}>{item.unit || '—'}</td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>{item.unit_price == null ? '—' : item.unit_price}</td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>{formatQty(item.safety_line)}</td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>{item.standing_stock == null ? '—' : formatQty(item.standing_stock)}</td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>{item.max_stock == null ? '—' : formatQty(item.max_stock)}</td>
                  <td style={tdStyle}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: '#3730a3' }}>{formatWeekdaysDisplay(item.weekdays)}</span>
                  </td>
                  <td style={tdStyle}>
                    <button type="button" onClick={() => toggleActive(item)} style={{
                      padding: '3px 10px', borderRadius: 999, border: 'none', fontSize: 13, fontWeight: 700, cursor: 'pointer',
                      background: item.is_active ? '#dcfce7' : '#fee2e2',
                      color: item.is_active ? '#16a34a' : '#dc2626',
                    }}>
                      {item.is_active ? '啟用' : '停用'}
                    </button>
                  </td>
                  <td style={{ ...tdStyle, position: 'sticky', right: 0, zIndex: 1, background: idx % 2 === 0 ? '#fff' : '#f8fafc', boxShadow: '-2px 0 0 #e5e7eb', whiteSpace: 'nowrap' }}>
                    <button type="button" onClick={() => startEdit(item)} style={btnSmall('#2563eb')}>編輯</button>
                    <button type="button" onClick={() => deleteItem(item)} style={btnSmall('#dc2626')}>刪除</button>
                  </td>
                </tr>
              )
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={12} style={{ ...tdStyle, textAlign: 'center', color: '#94a3b8', padding: 28 }}>無符合條件的商品</td></tr>
            )}
          </tbody>
        </table>
      </div>
      )}
    </div>
  );
}

const thStyle = { padding: '10px 10px', textAlign: 'left', fontWeight: 700, color: '#374151', whiteSpace: 'nowrap', fontSize: 13 };
const tdStyle = { padding: '8px 10px', borderTop: '1px solid #f1f5f9', fontSize: 14, verticalAlign: 'middle' };
const btnSmall = (bg) => ({
  padding: '4px 14px', borderRadius: 6, border: 'none', background: bg,
  color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer', marginRight: 4,
});

// 叫貨頁已抽離至 InventoryOrderView

// ========== 廠商設定 ==========
function VendorsTab({ vendors, reload, isMobile }) {
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [showAdd, setShowAdd] = useState(false);

  const inputStyle = {
    padding: '6px 10px', borderRadius: 6, border: '1px solid #d1d5db',
    fontSize: 15, width: '100%', boxSizing: 'border-box',
  };

  const emptyForm = () => ({
    name: '',
    delivery_weekdays: [],
    min_order_amount: '',
    order_cycle: '',
    note: '',
    is_active: true,
  });

  const startEdit = (v) => {
    setEditing(v.id);
    setForm({
      name: v.name || '',
      delivery_weekdays: Array.isArray(v.delivery_weekdays) ? [...v.delivery_weekdays] : [],
      min_order_amount: v.min_order_amount == null ? '' : String(v.min_order_amount),
      order_cycle: v.order_cycle || '',
      note: v.note || '',
      is_active: !!v.is_active,
    });
  };

  const saveEdit = async () => {
    if (!form.name?.trim()) return alert('請輸入廠商名稱');
    const payload = {
      name: form.name.trim(),
      delivery_weekdays: form.delivery_weekdays || [],
      min_order_amount: form.min_order_amount === '' || form.min_order_amount == null ? null : parseFloat(form.min_order_amount),
      order_cycle: form.order_cycle || '',
      note: form.note || '',
      is_active: form.is_active !== false,
    };
    await api(`/api/inventory/vendors/${editing}`, { method: 'PUT', body: JSON.stringify(payload) });
    setEditing(null); setForm({}); reload();
  };

  const addVendor = async () => {
    if (!form.name?.trim()) return alert('請輸入廠商名稱');
    const payload = {
      name: form.name.trim(),
      delivery_weekdays: form.delivery_weekdays || [],
      min_order_amount: form.min_order_amount === '' || form.min_order_amount == null ? null : parseFloat(form.min_order_amount),
      order_cycle: form.order_cycle || '',
      note: form.note || '',
      is_active: form.is_active !== false,
    };
    const res = await api('/api/inventory/vendors', { method: 'POST', body: JSON.stringify(payload) });
    if (!res.success) return alert(res.error || '新增失敗');
    setShowAdd(false); setForm({}); reload();
  };

  const deleteVendor = async (v) => {
    if (!window.confirm(`確定刪除廠商「${v.name}」？相關品項的廠商關聯會被清空。`)) return;
    await api(`/api/inventory/vendors/${v.id}`, { method: 'DELETE' });
    reload();
  };

  const toggleActive = async (v) => {
    await api(`/api/inventory/vendors/${v.id}`, {
      method: 'PUT',
      body: JSON.stringify({ is_active: !v.is_active }),
    });
    reload();
  };

  const formFields = (
    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(180px, 1fr))', gap: 10 }}>
      <input placeholder="廠商名稱 *" value={form.name || ''} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} style={inputStyle} />
      <input placeholder="低消金額" type="number" step="1" inputMode="decimal" value={form.min_order_amount ?? ''} onChange={e => setForm(p => ({ ...p, min_order_amount: e.target.value }))} style={inputStyle} />
      <input placeholder="叫貨週期（例：每14天）" value={form.order_cycle || ''} onChange={e => setForm(p => ({ ...p, order_cycle: e.target.value }))} style={inputStyle} />
      <input placeholder="備註" value={form.note || ''} onChange={e => setForm(p => ({ ...p, note: e.target.value }))} style={{ ...inputStyle, gridColumn: isMobile ? undefined : 'span 2' }} />
      <div style={{ gridColumn: '1 / -1' }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#475569', marginBottom: 6 }}>送貨／可叫貨星期</div>
        <WeekdayCheckboxes
          value={form.delivery_weekdays || []}
          onChange={ws => setForm(p => ({ ...p, delivery_weekdays: ws }))}
        />
      </div>
    </div>
  );

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
        <button type="button" onClick={() => { setShowAdd(true); setForm(emptyForm()); }}
          style={{ padding: '8px 18px', borderRadius: 6, border: 'none', background: '#2563eb', color: '#fff', fontWeight: 700, fontSize: 15, cursor: 'pointer' }}>
          + 新增廠商
        </button>
      </div>

      {showAdd && (
        <div style={{ background: '#eff6ff', borderRadius: 10, padding: 16, marginBottom: 14, border: '1px solid #bfdbfe' }}>
          <div style={{ fontWeight: 700, marginBottom: 10, fontSize: 16 }}>新增廠商</div>
          {formFields}
          <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
            <button type="button" onClick={addVendor} style={{ padding: '7px 20px', borderRadius: 6, border: 'none', background: '#16a34a', color: '#fff', fontWeight: 700, fontSize: 15, cursor: 'pointer' }}>確定新增</button>
            <button type="button" onClick={() => { setShowAdd(false); setForm({}); }} style={{ padding: '7px 20px', borderRadius: 6, border: '1px solid #d1d5db', background: '#fff', color: '#374151', fontWeight: 600, fontSize: 15, cursor: 'pointer' }}>取消</button>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {(vendors || []).map((v) => (
          editing === v.id ? (
            <div key={v.id} style={{ background: '#fffbeb', borderRadius: 10, border: '1px solid #fde68a', padding: 14 }}>
              {formFields}
              <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
                <button type="button" onClick={saveEdit} style={btnSmall('#16a34a')}>儲存</button>
                <button type="button" onClick={() => { setEditing(null); setForm({}); }} style={btnSmall('#6b7280')}>取消</button>
              </div>
            </div>
          ) : (
            <div key={v.id} style={{
              background: '#fff', borderRadius: 10, border: '1px solid #e5e7eb',
              padding: '12px 14px', opacity: v.is_active ? 1 : 0.5,
              display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', alignItems: 'flex-start',
            }}>
              <div style={{ flex: 1, minWidth: 200 }}>
                <div style={{ fontWeight: 800, fontSize: 16, marginBottom: 4 }}>{v.name}</div>
                <div style={{ fontSize: 14, color: '#475569', lineHeight: 1.6 }}>
                  <div>送貨星期：{formatWeekdaysDisplay(v.delivery_weekdays)}</div>
                  <div>低消：{v.min_order_amount == null ? '—' : Number(v.min_order_amount).toLocaleString('zh-TW')}</div>
                  {v.order_cycle ? <div>週期：{v.order_cycle}</div> : null}
                  {v.note ? <div>備註：{v.note}</div> : null}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                <button type="button" onClick={() => toggleActive(v)} style={{
                  padding: '3px 12px', borderRadius: 999, border: 'none', fontSize: 13, fontWeight: 700, cursor: 'pointer',
                  background: v.is_active ? '#dcfce7' : '#fee2e2',
                  color: v.is_active ? '#16a34a' : '#dc2626',
                }}>
                  {v.is_active ? '啟用' : '停用'}
                </button>
                <button type="button" onClick={() => startEdit(v)} style={btnSmall('#2563eb')}>編輯</button>
                <button type="button" onClick={() => deleteVendor(v)} style={btnSmall('#dc2626')}>刪除</button>
              </div>
            </div>
          )
        ))}
        {(vendors || []).length === 0 && (
          <div style={{ textAlign: 'center', color: '#94a3b8', padding: 28 }}>尚無廠商，請新增或執行資料轉換腳本</div>
        )}
      </div>
    </div>
  );
}

// ========== 位置頁 ==========
function LocationTab({ items, locations, reload, isMobile }) {
  const [newLocName, setNewLocName] = useState('');
  const [editingLoc, setEditingLoc] = useState(null);
  const [editName, setEditName] = useState('');
  const [dragItem, setDragItem] = useState(null);
  const [dragOverLoc, setDragOverLoc] = useState(null);
  const [dragOverItemId, setDragOverItemId] = useState(null);

  const addLocation = async () => {
    if (!newLocName.trim()) return;
    await api('/api/inventory/locations', { method: 'POST', body: JSON.stringify({ name: newLocName.trim() }) });
    setNewLocName(''); reload();
  };
  const updateLocation = async (id) => {
    if (!editName.trim()) return;
    await api(`/api/inventory/locations/${id}`, { method: 'PUT', body: JSON.stringify({ name: editName.trim() }) });
    setEditingLoc(null); reload();
  };
  const deleteLocation = async (id, name) => {
    if (!window.confirm(`確定刪除位置「${name}」？該位置下的商品將變為未分配。`)) return;
    await api(`/api/inventory/locations/${id}`, { method: 'DELETE' }); reload();
  };

  const moveLocation = async (locIdx, direction) => {
    const targetIdx = locIdx + direction;
    if (targetIdx < 0 || targetIdx >= locations.length) return;
    const orders = locations.map((l, i) => {
      let newSort = i;
      if (i === locIdx) newSort = targetIdx;
      else if (i === targetIdx) newSort = locIdx;
      return { id: l.id, sort_order: newSort };
    });
    await api('/api/inventory/locations/batch-sort', {
      method: 'PUT', body: JSON.stringify({ orders }),
    });
    reload();
  };

  const grouped = useMemo(() => {
    const map = { _unassigned: [] };
    locations.forEach(l => { map[l.id] = []; });
    items.forEach(item => {
      const key = item.location_id || '_unassigned';
      if (!map[key]) map[key] = [];
      map[key].push(item);
    });
    return map;
  }, [items, locations]);

  const scrollContainerRef = useRef(null);
  const scrollRafRef = useRef(null);

  const handleDragStart = (e, item) => {
    setDragItem(item);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(item.id));
  };
  const handleDragOver = (e, locKey) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverLoc(locKey);
  };
  const handleDragLeave = () => setDragOverLoc(null);

  const handleDrop = async (e, locKey, dropBeforeItemId) => {
    e.preventDefault();
    setDragOverLoc(null);
    setDragOverItemId(null);
    if (!dragItem) return;
    const newLocId = locKey === '_unassigned' ? null : locKey;
    const oldLocId = dragItem.location_id || null;
    const oldLocKey = oldLocId ?? '_unassigned';

    if (newLocId === oldLocId && locKey === oldLocKey) {
      const zoneItems = [...(grouped[locKey] || [])];
      const fromIdx = zoneItems.findIndex(i => i.id === dragItem.id);
      if (fromIdx === -1) { setDragItem(null); return; }
      zoneItems.splice(fromIdx, 1);
      if (dropBeforeItemId) {
        const toIdx = zoneItems.findIndex(i => i.id === dropBeforeItemId);
        if (toIdx !== -1) zoneItems.splice(toIdx, 0, dragItem);
        else zoneItems.push(dragItem);
      } else {
        zoneItems.push(dragItem);
      }
      const moves = zoneItems.map((item, idx) => ({ id: item.id, sort_order: idx }));
      await api('/api/inventory/items/batch-location', {
        method: 'PUT', body: JSON.stringify({ moves }),
      });
    } else {
      const targetItems = [...(grouped[locKey] || [])];
      let sortOrder = targetItems.length;
      if (dropBeforeItemId) {
        const toIdx = targetItems.findIndex(i => i.id === dropBeforeItemId);
        if (toIdx !== -1) sortOrder = toIdx;
      }
      const moves = [{ id: dragItem.id, location_id: newLocId, sort_order: sortOrder }];
      targetItems.forEach((item, idx) => {
        const newIdx = idx >= sortOrder ? idx + 1 : idx;
        if (newIdx !== idx) moves.push({ id: item.id, sort_order: newIdx });
      });
      await api('/api/inventory/items/batch-location', {
        method: 'PUT', body: JSON.stringify({ moves }),
      });
    }
    setDragItem(null);
    reload();
  };

  const handleScrollAreaDrag = useCallback((e) => {
    const container = scrollContainerRef.current;
    if (!container || !dragItem) return;
    const rect = container.getBoundingClientRect();
    const edgeZone = 80;
    const x = e.clientX;

    cancelAnimationFrame(scrollRafRef.current);
    if (x < rect.left + edgeZone) {
      const speed = Math.max(8, (edgeZone - (x - rect.left)) * 0.5);
      const scroll = () => { container.scrollLeft -= speed; scrollRafRef.current = requestAnimationFrame(scroll); };
      scrollRafRef.current = requestAnimationFrame(scroll);
    } else if (x > rect.right - edgeZone) {
      const speed = Math.max(8, (edgeZone - (rect.right - x)) * 0.5);
      const scroll = () => { container.scrollLeft += speed; scrollRafRef.current = requestAnimationFrame(scroll); };
      scrollRafRef.current = requestAnimationFrame(scroll);
    }
  }, [dragItem]);

  const handleScrollAreaDragEnd = useCallback(() => {
    cancelAnimationFrame(scrollRafRef.current);
  }, []);

  const ZONE_COLORS = [
    { bg: '#f0fdf4', border: '#86efac', headerBg: '#16a34a' },
    { bg: '#eff6ff', border: '#93c5fd', headerBg: '#2563eb' },
    { bg: '#fefce8', border: '#fde047', headerBg: '#ca8a04' },
    { bg: '#fdf2f8', border: '#f9a8d4', headerBg: '#db2777' },
    { bg: '#f5f3ff', border: '#c4b5fd', headerBg: '#7c3aed' },
    { bg: '#ecfeff', border: '#67e8f9', headerBg: '#0891b2' },
    { bg: '#fff7ed', border: '#fdba74', headerBg: '#ea580c' },
    { bg: '#f0fdfa', border: '#5eead4', headerBg: '#0d9488' },
  ];
  const getZoneColor = (idx) => ZONE_COLORS[idx % ZONE_COLORS.length];

  const zoneStyle = (isOver, colorSet) => ({
    minHeight: 70, padding: 12, borderRadius: 10,
    border: `2px dashed ${isOver ? '#2563eb' : (colorSet?.border || '#d1d5db')}`,
    background: isOver ? '#eff6ff' : (colorSet?.bg || '#f9fafb'),
    transition: 'all .15s',
    display: 'flex', flexWrap: 'wrap', gap: 8,
    alignContent: 'flex-start',
  });

  const moveItemToLocation = async (itemId, newLocId) => {
    const moves = [{ id: itemId, location_id: newLocId === '' ? null : Number(newLocId) }];
    await api('/api/inventory/items/batch-location', {
      method: 'PUT', body: JSON.stringify({ moves }),
    });
    reload();
  };

  const chipStyle = {
    padding: '7px 14px', borderRadius: 8, background: '#fff', border: '1px solid #e5e7eb',
    fontSize: 15, fontWeight: 600, cursor: isMobile ? 'default' : 'grab', userSelect: 'none',
    boxShadow: '0 1px 3px rgba(0,0,0,.06)',
  };

  const arrowBtnStyle = (disabled) => ({
    padding: '2px 6px', borderRadius: 4, border: '1px solid #cbd5e1',
    background: disabled ? '#f1f5f9' : '#fff', color: disabled ? '#cbd5e1' : '#475569',
    fontSize: 14, fontWeight: 700, cursor: disabled ? 'default' : 'pointer',
    lineHeight: 1, opacity: disabled ? 0.5 : 1,
  });

  const renderZone = (locKey, itemsList, colorSet, opts = {}) => (
    <div
      style={{
        ...zoneStyle(dragOverLoc === locKey, colorSet),
        ...(opts.style || {}),
        ...(isMobile ? { flexDirection: 'column' } : {}),
      }}
      {...(isMobile ? {} : {
        onDragOver: e => handleDragOver(e, locKey),
        onDragLeave: () => { setDragOverLoc(null); setDragOverItemId(null); },
        onDrop: e => handleDrop(e, locKey),
      })}
    >
      {itemsList.length === 0 ? (
        <span style={{ color: '#94a3b8', fontSize: 15, padding: 10 }}>{isMobile ? '此區無商品' : '拖拉商品到此處'}</span>
      ) : (
        itemsList.map(item => (
          <div key={item.id}
            {...(isMobile ? {} : {
              draggable: true,
              onDragStart: e => handleDragStart(e, item),
              onDragOver: e => { e.preventDefault(); e.stopPropagation(); setDragOverItemId(item.id); setDragOverLoc(locKey); },
              onDrop: e => { e.stopPropagation(); handleDrop(e, locKey, item.id); },
            })}
            style={{
              ...chipStyle,
              opacity: dragItem?.id === item.id ? 0.4 : 1,
              borderLeft: dragOverItemId === item.id && dragItem?.id !== item.id ? '3px solid #2563eb' : '1px solid #e5e7eb',
              ...(isMobile ? { display: 'flex', alignItems: 'center', gap: 8, width: '100%', boxSizing: 'border-box' } : {}),
            }}>
            <span style={{ flex: 1 }}>
              {item.name}
              <span style={{ fontSize: 13, color: '#94a3b8', marginLeft: 6 }}>{item.unit}</span>
            </span>
            {isMobile && (
              <select
                value={locKey === '_unassigned' ? '' : String(locKey)}
                onChange={e => moveItemToLocation(item.id, e.target.value)}
                style={{ padding: '4px 6px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 13, flexShrink: 0 }}
              >
                <option value="">未分配</option>
                {locations.map(l => <option key={l.id} value={String(l.id)}>{l.name}</option>)}
              </select>
            )}
          </div>
        ))
      )}
    </div>
  );

  return (
    <div>
      {/* 新增位置 */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 14, alignItems: 'center' }}>
        <input placeholder="新位置名稱" value={newLocName} onChange={e => setNewLocName(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && addLocation()}
          style={{ padding: '7px 12px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 16, width: 200 }} />
        <button type="button" onClick={addLocation}
          style={{ padding: '7px 18px', borderRadius: 6, border: 'none', background: '#2563eb', color: '#fff', fontWeight: 700, fontSize: 15, cursor: 'pointer' }}>
          新增位置
        </button>
      </div>

      {/* 未分配區 — 固定在上方 */}
      <div style={{
        marginBottom: 16, padding: 12, background: '#f9fafb',
        borderRadius: 10, border: '1px solid #e5e7eb',
      }}>
        <div style={{ fontSize: 17, fontWeight: 700, color: '#94a3b8', marginBottom: 8 }}>
          未分配 <span style={{ fontSize: 14 }}>({(grouped._unassigned || []).length} 項)</span>
        </div>
        {renderZone('_unassigned', grouped._unassigned || [], null)}
      </div>

      {/* 位置卡片 */}
      <div
        ref={scrollContainerRef}
        {...(isMobile ? {} : {
          onDragOver: handleScrollAreaDrag,
          onDragLeave: handleScrollAreaDragEnd,
          onDrop: handleScrollAreaDragEnd,
          onDragEnd: handleScrollAreaDragEnd,
        })}
        style={isMobile ? {
          display: 'flex', flexDirection: 'column', gap: 14,
        } : {
          display: 'flex', gap: 16, overflowX: 'auto',
          padding: '8px 4px 14px',
          scrollSnapType: 'x proximity',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        {locations.map((loc, locIdx) => {
          const colorSet = getZoneColor(locIdx);
          const isOver = dragOverLoc === loc.id;
          return (
            <div
              key={loc.id}
              {...(isMobile ? {} : {
                onDragOver: e => handleDragOver(e, loc.id),
                onDragLeave: handleDragLeave,
                onDrop: e => handleDrop(e, loc.id),
              })}
              style={isMobile ? {
                width: '100%', borderRadius: 10,
                border: `2px solid ${colorSet.border}`,
                background: '#fff',
                display: 'flex', flexDirection: 'column',
              } : {
                minWidth: 300, maxWidth: 360, width: 320, flexShrink: 0,
                height: 460, maxHeight: '62vh',
                borderRadius: 10,
                border: `2px solid ${isOver ? '#2563eb' : colorSet.border}`,
                background: isOver ? '#dbeafe' : '#fff',
                display: 'flex', flexDirection: 'column',
                transition: 'background .15s, border-color .15s, box-shadow .15s',
                boxShadow: isOver ? '0 0 0 3px rgba(37,99,235,.25)' : 'none',
                scrollSnapAlign: 'start',
              }}
            >
              {/* 位置標題 */}
              <div style={{
                display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap',
                padding: '10px 12px',
                borderRadius: '8px 8px 0 0',
                background: isOver ? '#93c5fd' : colorSet.headerBg + '18',
                borderBottom: `1px solid ${isOver ? '#60a5fa' : colorSet.border}`,
                transition: 'background .15s',
                flexShrink: 0,
              }}>
                {editingLoc === loc.id ? (
                  <>
                    <input value={editName} onChange={e => setEditName(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && updateLocation(loc.id)}
                      style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 15, fontWeight: 700, width: '100%' }} autoFocus />
                    <button type="button" onClick={() => updateLocation(loc.id)} style={btnSmall('#16a34a')}>儲存</button>
                    <button type="button" onClick={() => setEditingLoc(null)} style={btnSmall('#6b7280')}>取消</button>
                  </>
                ) : (
                  <>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
                      <button type="button" disabled={locIdx === 0}
                        onClick={() => moveLocation(locIdx, -1)}
                        style={arrowBtnStyle(locIdx === 0)}>{isMobile ? '▲' : '◀'}</button>
                      <button type="button" disabled={locIdx === locations.length - 1}
                        onClick={() => moveLocation(locIdx, 1)}
                        style={arrowBtnStyle(locIdx === locations.length - 1)}>{isMobile ? '▼' : '▶'}</button>
                    </div>
                    <span style={{
                      display: 'inline-block', width: 10, height: 10, borderRadius: '50%',
                      background: colorSet.headerBg, flexShrink: 0,
                    }} />
                    <span style={{ fontSize: 16, fontWeight: 700, color: '#1e293b' }}>{loc.name}</span>
                    <span style={{ fontSize: 13, color: '#94a3b8' }}>({(grouped[loc.id] || []).length})</span>
                    <div style={{ marginLeft: 'auto', display: 'flex', gap: 4 }}>
                      <button type="button" onClick={() => { setEditingLoc(loc.id); setEditName(loc.name); }} style={btnSmall('#64748b')}>改名</button>
                      <button type="button" onClick={() => deleteLocation(loc.id, loc.name)} style={btnSmall('#dc2626')}>刪除</button>
                    </div>
                  </>
                )}
              </div>
              {/* 拖放區 */}
              <div style={{ flex: 1, padding: 4, overflowY: 'auto', ...(isMobile ? { maxHeight: '40vh' } : {}) }}>
                {renderZone(loc.id, grouped[loc.id] || [], colorSet, {
                  style: { minHeight: isMobile ? 40 : 100, borderRadius: 8, border: 'none', background: 'transparent' },
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ========== 主元件 ==========
const TABS = [
  { key: 'items', label: '商品明細' },
  { key: 'order', label: '叫貨' },
  { key: 'vendor', label: '廠商設定' },
  { key: 'sales', label: '銷售' },
  { key: 'revenue', label: '營收' },
  { key: 'location', label: '位置編輯' },
];

export default function InventoryAdmin() {
  const [tab, setTab] = useState('items');
  const [items, setItems] = useState([]);
  const [locations, setLocations] = useState([]);
  const [vendors, setVendors] = useState([]);
  const mountRef = useRef(true);

  const [vpW, setVpW] = useState(() => typeof window !== 'undefined' ? window.innerWidth : 1024);
  useEffect(() => {
    const h = () => setVpW(window.innerWidth);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  const isMobile = vpW < 768;

  const loadAll = useCallback(async () => {
    try {
      const [locRes, itemRes, vendorRes] = await Promise.all([
        api('/api/inventory/locations'),
        api('/api/inventory/items'),
        api('/api/inventory/vendors'),
      ]);
      if (!mountRef.current) return;
      if (locRes.success) setLocations(locRes.list);
      if (itemRes.success) setItems(itemRes.list);
      if (vendorRes.success) setVendors(vendorRes.list);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => {
    mountRef.current = true;
    loadAll();
    return () => { mountRef.current = false; };
  }, [loadAll]);

  useEffect(() => {
    const onItems = () => loadAll();
    const onLocs = () => loadAll();
    const onVendors = () => loadAll();
    socket.on('inventory:items', onItems);
    socket.on('inventory:locations', onLocs);
    socket.on('inventory:vendors', onVendors);
    return () => {
      socket.off('inventory:items', onItems);
      socket.off('inventory:locations', onLocs);
      socket.off('inventory:vendors', onVendors);
    };
  }, [loadAll]);

  const tabBtnStyle = (active) => ({
    padding: isMobile ? '6px 12px' : '8px 22px', borderRadius: 8, border: 'none',
    background: active ? '#1e293b' : '#e2e8f0',
    color: active ? '#fff' : '#475569',
    fontWeight: 700, fontSize: isMobile ? 14 : 16, cursor: 'pointer',
    transition: 'all .15s',
    flexShrink: 0,
  });

  return (
    <div style={{ maxWidth: 1400, margin: '0 auto', minWidth: 0, width: '100%', padding: isMobile ? '0 4px' : 0, boxSizing: 'border-box' }}>
      <div style={{
        position: 'sticky', top: 0, zIndex: 40,
        background: '#fff', padding: isMobile ? '8px 0 10px' : '10px 0 12px',
        borderBottom: '1px solid #e5e7eb',
        marginBottom: isMobile ? 10 : 14,
        display: 'flex', gap: isMobile ? 6 : 8, flexWrap: 'wrap', alignItems: 'center',
      }}>
        {TABS.map(t => (
          <button key={t.key} type="button" style={tabBtnStyle(tab === t.key)} onClick={() => setTab(t.key)}>
            {t.label}
          </button>
        ))}
        <a
          href="/inventory"
          target="_blank"
          rel="noopener noreferrer"
          style={{
            padding: isMobile ? '6px 12px' : '8px 22px', borderRadius: 8,
            border: '1px solid #075985', background: '#0369a1', color: '#fff',
            fontWeight: 700, fontSize: isMobile ? 13 : 16, textDecoration: 'none',
            ...(isMobile ? {} : { marginLeft: 'auto' }),
          }}
        >
          前往點貨前台 ↗
        </a>
      </div>
      {tab === 'items' && <ItemsTab items={items} locations={locations} vendors={vendors} reload={loadAll} isMobile={isMobile} />}
      {tab === 'order' && <InventoryOrderView compact={isMobile} />}
      {tab === 'vendor' && <VendorsTab vendors={vendors} reload={loadAll} isMobile={isMobile} />}
      {tab === 'sales' && <div style={{ overflowX: 'auto', minWidth: 0 }}><SalesConsumptionView auth="admin" /></div>}
      {tab === 'revenue' && <div style={{ overflowX: 'auto', minWidth: 0 }}><SalesRevenueView auth="admin" /></div>}
      {tab === 'location' && <LocationTab items={items} locations={locations} reload={loadAll} isMobile={isMobile} />}
    </div>
  );
}
